import { Matrix4, Quaternion, Vector3 } from "three";
import type { Vec3 } from "@models/data/entityData";
import type { ParticipantQuat } from "@models/multiuser/IMultiuserSessionModel";
import { unityEulerToQuat, unityQuatToEuler } from "@models/utils/unityBasis";
import { threeWorldQuaternionToUnity } from "./participantTransformConversion";

/**
 * Unity `Quaternion.Euler(x, y, z)` — degrees, ZXY application order:
 * `q = Ry(y) · Rx(x) · Rz(z)`. Feed the returned Unity-space quaternion through
 * `unityWorldQuaternionToThree` (`participantTransformConversion.ts`) to place a
 * world-space object — the same two-step path the presence avatars use.
 *
 * Delegates to `@models/utils/unityBasis`; this used to be one of three separate
 * implementations of Unity's euler composition (task 0004).
 *
 * (Still not interchangeable with `unityEulerToThreeQuat` in
 * `snapshotRotationConversion.ts` — that one lands in the NODE frame and skips
 * the forward-convention adapter that world poses need.)
 */
export function unityEulerToUnityQuaternion(eulerDegrees: Vec3): ParticipantQuat {
  return unityEulerToQuat(eulerDegrees) as ParticipantQuat;
}

/**
 * Store any Three.js world-space orientation as Unity `Quaternion.eulerAngles`.
 *
 * Converts to Unity space with the world-pose map and decomposes the equivalent
 * `q = Ry · Rx · Rz` rotation, so the persisted Euler fields feed back through
 * `unityEulerToUnityQuaternion` + `unityWorldQuaternionToThree` to exactly the
 * quaternion passed in (both conversions are involutions).
 *
 * Range is Unity's `[0, 360)`, matching `Quaternion.eulerAngles` — unlike the
 * entity-snapshot path, which persists a signed range for inspector display.
 */
export function threeWorldQuaternionToUnityEuler(quaternion: Quaternion): Vec3 {
  const unity = threeWorldQuaternionToUnity(quaternion);
  const normalized = new Quaternion(...unity).normalize();
  return unityQuatToEuler([normalized.x, normalized.y, normalized.z, normalized.w], "unity");
}

/**
 * Store a Three.js camera orientation as Unity `Quaternion.eulerAngles`.
 *
 * Unity cards use `Quaternion.LookRotation(CameraLookAt - CameraPosition)`.
 * The orbit camera already carries that orientation in Three world space, so
 * this is the world-quaternion path applied to the camera pose.
 */
export function threeCameraQuaternionToUnityEuler(
  cameraQuaternion: Quaternion,
): Vec3 {
  return threeWorldQuaternionToUnityEuler(cameraQuaternion);
}

/**
 * Unity `SurfacePlacementHelper` port: orient a card upright against a
 * surface. The card's front face is its local +Z (see `MediaTileRenderer`
 * billboard), so build a Three world basis with local +Z aligned to the
 * outward surface normal and local +Y as close to world-up as possible,
 * then persist as Unity Euler. For a near-horizontal surface the up
 * reference falls back to world +Z (mirrors Unity's `ResolveUp`).
 */
export function surfaceAlignedTileEuler(worldNormal: Vector3): Vec3 {
  const forward = worldNormal.clone().normalize();
  const worldUp = new Vector3(0, 1, 0);
  const referenceUp = Math.abs(forward.dot(worldUp)) > 0.98
    ? new Vector3(0, 0, 1)
    : worldUp;
  const right = new Vector3().crossVectors(referenceUp, forward).normalize();
  const up = new Vector3().crossVectors(forward, right).normalize();
  const basis = new Matrix4().makeBasis(right, up, forward);
  const rotation = new Quaternion().setFromRotationMatrix(basis);
  return threeWorldQuaternionToUnityEuler(rotation);
}

